// Configuration de l'API
const API_BASE = window.location.origin;

// Éléments DOM
const createForm = document.getElementById('createForm');
const nameInput = document.getElementById('nameInput');
const domainSelect = document.getElementById('domainSelect');
const progressSection = document.getElementById('progressSection');
const progressLogs = document.getElementById('progressLogs');
const instancesList = document.getElementById('instancesList');
const refreshBtn = document.getElementById('refreshBtn');
const filterInput = document.getElementById('filterInput');
const logoutBtn = document.getElementById('logoutBtn');
const userInfo = document.getElementById('userInfo');

// État de l'application
let currentEventSource = null;
let currentUser = null;
let filterDebounceTimer = null;

/**
 * Vérifie l'authentification
 */
async function checkAuth() {
    try {
        const response = await fetch(`${API_BASE}/api/auth/check`, {
            credentials: 'include'
        });
        const data = await response.json();
        
        if (!data.authenticated) {
            window.location.href = '/login.html';
            return false;
        }
        
        currentUser = data.user;
        if (userInfo) {
            userInfo.textContent = `👤 ${currentUser.username}`;
        }
        return true;
    } catch (error) {
        console.error('Auth check failed:', error);
        window.location.href = '/login.html';
        return false;
    }
}

/**
 * Gère la déconnexion
 */
async function logout() {
    try {
        await fetch(`${API_BASE}/api/auth/logout`, {
            method: 'POST',
            credentials: 'include'
        });
        window.location.href = '/login.html';
    } catch (error) {
        console.error('Logout failed:', error);
        window.location.href = '/login.html';
    }
}

// Gestionnaire de déconnexion
if (logoutBtn) {
    logoutBtn.addEventListener('click', logout);
}

/**
 * Charge et affiche les instances existantes (chargement progressif)
 */
async function loadInstances() {
    instancesList.innerHTML = '<div class="loading">Chargement...</div>';

    try {
        const filter = filterInput ? filterInput.value.trim() : '';
        const query = filter ? `?filter=${encodeURIComponent(filter)}` : '';
        const response = await fetch(`${API_BASE}/api/instances${query}`, {
            credentials: 'include'
        });
        const data = await response.json();
        
        if (data.success) {
            if (data.instances.length === 0) {
                instancesList.innerHTML = '<div class="loading">Aucune instance détectée</div>';
                return;
            }
            
            // Afficher immédiatement les instances avec des placeholders
            displayInstancesProgressive(data.instances);
            
            // Charger version + lock status en parallèle pour chaque instance
            data.instances.forEach(instance => {
                loadInstanceDetails(instance.domain);
            });
        } else {
            instancesList.innerHTML = `<div class="error">Erreur: ${data.error}</div>`;
        }
    } catch (error) {
        instancesList.innerHTML = `<div class="error">Erreur de chargement: ${error.message}</div>`;
    }
}

/**
 * Affiche les instances avec placeholders (chargement progressif)
 */
function displayInstancesProgressive(instances) {
    instancesList.innerHTML = instances.map(instance => createInstanceCard(instance, true)).join('');
}

/**
 * Charge les détails d'une instance (version + lock) et met à jour sa carte
 */
async function loadInstanceDetails(domain) {
    try {
        // Charger version et lock status en parallèle
        const [versionResponse, lockResponse] = await Promise.all([
            fetch(`${API_BASE}/api/instances/${domain}/version`, { credentials: 'include' }),
            fetch(`${API_BASE}/api/instances/${domain}/lock`, { credentials: 'include' })
        ]);
        
        const versionData = await versionResponse.json();
        const lockData = await lockResponse.json();
        
        // Trouver l'instance complète dans le DOM pour avoir path, status, url
        const card = document.querySelector(`[data-domain="${domain}"]`);
        if (!card) return;
        
        const instance = {
            domain,
            path: card.dataset.path,
            status: card.dataset.status,
            url: card.dataset.url,
            version: versionData.success ? versionData.current : null,
            latestVersion: versionData.success ? versionData.latest : null,
            upgradeAvailable: versionData.success ? versionData.upgradeAvailable : false,
            locked: lockData.success ? lockData.locked : false
        };
        
        // Mettre à jour la carte
        updateInstanceCard(domain, instance);
    } catch (error) {
        console.error(`Error loading details for ${domain}:`, error);
    }
}

/**
 * Recharge une seule instance (après action)
 */
async function refreshInstance(domain) {
    await loadInstanceDetails(domain);
}

/**
 * Crée le HTML d'une carte d'instance
 * @param {Object} instance - Instance data
 * @param {Boolean} skeleton - Si true, affiche des placeholders
 */
function createInstanceCard(instance, skeleton = false) {
    const locked = skeleton ? false : instance.locked;
    const version = skeleton ? null : instance.version;
    const upgradeAvailable = skeleton ? false : instance.upgradeAvailable;
    
    return `
        <div class="instance-card ${!locked ? 'unlocked-warning' : ''}" 
             data-domain="${instance.domain}"
             data-path="${instance.path || ''}"
             data-status="${instance.status || ''}"
             data-url="${instance.url || ''}">
            <div class="instance-header">
                <div class="instance-domain">${instance.domain}</div>
                <div style="display: flex; gap: 10px; align-items: center;">
                    ${skeleton ? '<div class="skeleton-btn"></div>' : ''}
                    ${!skeleton && upgradeAvailable && !locked ? 
                        `<button class="btn-upgrade" onclick="startUpgrade('${instance.domain}', '${instance.version}', '${instance.latestVersion}')" title="Mettre à jour vers ${instance.latestVersion}">
                            ⬆️ Mise à jour
                        </button>` : ''}
                    ${!skeleton && upgradeAvailable && locked ?
                        `<button class="btn-upgrade" disabled title="Déverrouillez d'abord l'instance">
                            🔒 Mise à jour
                        </button>` : ''}
                    <button class="btn-action" onclick="startBackup('${instance.domain}')" title="Créer un backup">💾 Backup</button>
                    <button class="btn-action" onclick="showRestore('${instance.domain}')" title="Restaurer depuis un backup">♻️ Restore</button>
                    <button class="btn-config" onclick="showConfig('${instance.domain}')">⚙️ Config</button>
                    ${skeleton ? '<div class="skeleton-btn"></div>' : 
                        `<button class="btn-lock ${locked ? 'locked' : 'unlocked'}" onclick="toggleLock('${instance.domain}', ${locked})" title="${locked ? 'Déverrouiller' : 'Verrouiller'}">
                            ${locked ? '🔒' : '🔓'}
                        </button>`
                    }
                    <span class="instance-status status-${instance.status}">
                        ${getStatusLabel(instance.status)}
                    </span>
                </div>
            </div>
            ${!locked && !skeleton ? '<div class="lock-warning">⚠️ Instance déverrouillée - Verrouillez après avoir terminé l\'installation/mise à jour</div>' : ''}
            <div class="instance-info">
                <div>📁 ${instance.path || ''}</div>
                <div>🌐 <a href="${instance.url || ''}" target="_blank" class="instance-url">${instance.url || ''}</a></div>
                ${skeleton ? '<div class="skeleton-text"></div>' : 
                    version ? `<div>📦 Version: <span class="version-badge">${version}</span>${upgradeAvailable ? ` → <span class="version-badge version-available">${instance.latestVersion}</span>` : ''}</div>` : ''}
            </div>
            <!-- Instance-specific logs section -->
            <div class="instance-logs" id="logs-${instance.domain}" style="display: none;">
                <div class="logs-header">
                    <span class="logs-title">📋 Logs</span>
                    <button class="btn-close-logs" id="close-logs-${instance.domain}" onclick="closeInstanceLogs('${instance.domain}')" style="display: none;">[x]</button>
                </div>
                <div class="logs-content" id="logs-content-${instance.domain}"></div>
            </div>
        </div>
    `;
}

/**
 * Met à jour une carte d'instance existante
 */
function updateInstanceCard(domain, instance) {
    const card = document.querySelector(`[data-domain="${domain}"]`);
    if (!card) return;
    
    // Remplacer la carte avec la version complète
    card.outerHTML = createInstanceCard(instance, false);
}

/**
 * Retourne le label du status
 */
function getStatusLabel(status) {
    const labels = {
        'running': '✅ En ligne',
        'pending-setup': '🟠 Config requise',
        'installed': '🟡 Installé',
        'config-only': '🔴 Config seule',
        'unknown': '⚪ Inconnu'
    };
    return labels[status] || status;
}

/**
 * Ajoute un log dans la zone de progression
 */
function addLog(message, className = '') {
    const logEntry = document.createElement('div');
    logEntry.className = `log-entry ${className}`;
    logEntry.textContent = message;
    progressLogs.appendChild(logEntry);
    progressLogs.scrollTop = progressLogs.scrollHeight;
}

/**
 * Ajoute un log dans la section logs d'une instance spécifique
 */
function addInstanceLog(domain, message, className = '') {
    const logsSection = document.getElementById(`logs-${domain}`);
    const logsContent = document.getElementById(`logs-content-${domain}`);
    
    if (!logsSection || !logsContent) return;
    
    // Afficher la section si cachée
    if (logsSection.style.display === 'none') {
        logsSection.style.display = 'block';
    }
    
    const logEntry = document.createElement('div');
    logEntry.className = `log-entry ${className}`;
    logEntry.textContent = message;
    logsContent.appendChild(logEntry);
    logsContent.scrollTop = logsContent.scrollHeight;
}

/**
 * Ferme la section logs d'une instance et rafraîchit son état
 */
window.closeInstanceLogs = async function(domain) {
    const logsSection = document.getElementById(`logs-${domain}`);
    const logsContent = document.getElementById(`logs-content-${domain}`);
    
    if (logsSection) {
        logsSection.style.display = 'none';
    }
    
    if (logsContent) {
        logsContent.innerHTML = '';
    }
    
    // Rafraîchir l'instance pour afficher la nouvelle version
    await refreshInstance(domain);
};

/**
 * Charge les domaines disponibles
 */
async function loadDomains() {
    try {
        const response = await fetch(`${API_BASE}/api/domains`, {
            credentials: 'include'
        });
        const data = await response.json();
        
        if (data.success && data.domains && data.domains.length > 0) {
            domainSelect.innerHTML = '<option value="">-- Sélectionner un domaine --</option>' +
                data.domains.map(d => `<option value="${d.id}">${d.domain}</option>`).join('');
        } else {
            domainSelect.innerHTML = '<option value="">Aucun domaine configuré</option>';
        }
    } catch (error) {
        console.error('Error loading domains:', error);
        domainSelect.innerHTML = '<option value="">Erreur de chargement</option>';
    }
}

/**
 * Gère la soumission du formulaire de création
 */
createForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    
    const name = nameInput.value.trim();
    const domain_id = domainSelect.value;
    
    if (!name || !domain_id) {
        alert('Veuillez remplir tous les champs');
        return;
    }
    
    // Construire le FQDN pour l'affichage
    const selectedDomain = domainSelect.options[domainSelect.selectedIndex].text;
    const fullDomain = `${name}.${selectedDomain}`;
    
    // Désactiver le formulaire
    nameInput.disabled = true;
    domainSelect.disabled = true;
    createForm.querySelector('button').disabled = true;
    
    // Afficher la section de progression
    progressSection.classList.remove('hidden');
    progressLogs.innerHTML = '';
    
    addLog(`🚀 Création de l'instance ${fullDomain}...`, 'step');
    
    try {
        // Lancer la création
        const response = await fetch(`${API_BASE}/api/instances`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ name, domain_id })
        });
        
        const data = await response.json();
        
        if (!data.success) {
            throw new Error(data.error);
        }
        
        const jobId = data.jobId;
        addLog(`✓ Tâche créée (ID: ${jobId})`, 'step');
        
        // Connecter au flux SSE pour les updates
        currentEventSource = new EventSource(`${API_BASE}/api/events/${jobId}`);
        
        currentEventSource.onmessage = (event) => {
            const data = JSON.parse(event.data);
            
            switch (data.type) {
                case 'connected':
                    addLog('✓ Connexion au flux d\'événements établie', 'step');
                    break;
                
                case 'step':
                    addLog(`▶ ${data.message}`, 'step');
                    break;
                
                case 'log':
                    addLog(data.message);
                    break;
                
                case 'complete':
                    addLog(`\n✅ ${data.message}`, 'complete');
                    if (data.instanceInfo) {
                        addLog(`\n📋 Informations de l'instance:`, 'step');
                        addLog(`   URL: ${data.instanceInfo.url}`);
                        addLog(`   DB: ${data.instanceInfo.database.name}`);
                        addLog(`   User: ${data.instanceInfo.database.user}`);
                        addLog(`   Password: ${data.instanceInfo.database.password}`);
                    }
                    currentEventSource.close();
                    
                    // Réactiver le formulaire et recharger les instances
                    setTimeout(() => {
                        nameInput.disabled = false;
                        domainSelect.disabled = false;
                        createForm.querySelector('button').disabled = false;
                        domainInput.value = '';
                        loadInstances();
                    }, 2000);
                    break;
                
                case 'error':
                    addLog(`\n❌ Erreur: ${data.error}`, 'error');
                    currentEventSource.close();
                    nameInput.disabled = false;
                    domainSelect.disabled = false;
                    createForm.querySelector('button').disabled = false;
                    break;
            }
        };
        
        currentEventSource.onerror = (error) => {
            console.error('SSE Error:', error);
            addLog('❌ Erreur de connexion au flux d\'événements', 'error');
            currentEventSource.close();
            nameInput.disabled = false;
            domainSelect.disabled = false;
            createForm.querySelector('button').disabled = false;
        };
        
    } catch (error) {
        addLog(`❌ Erreur: ${error.message}`, 'error');
        nameInput.disabled = false;
        domainSelect.disabled = false;
        createForm.querySelector('button').disabled = false;
    }
});

/**
 * Bouton rafraîchir
 */
refreshBtn.addEventListener('click', loadInstances);

/**
 * Filtre avec debounce
 */
if (filterInput) {
    filterInput.addEventListener('input', () => {
        clearTimeout(filterDebounceTimer);
        filterDebounceTimer = setTimeout(loadInstances, 300);
    });
}

/**
 * Affiche la configuration d'une instance
 */
window.showConfig = async function(domain) {
    const modal = document.getElementById('configModal');
    const modalBody = document.getElementById('configModalBody');
    
    // Afficher la modale avec loading
    modal.classList.remove('hidden');
    modalBody.innerHTML = '<div class="loading">Chargement de la configuration...</div>';
    
    try {
        const response = await fetch(`${API_BASE}/api/instances/${domain}/config`);
        const data = await response.json();
        
        if (!data.success) {
            modalBody.innerHTML = `<div class="error">❌ ${data.error}</div>`;
            return;
        }
        
        const config = data.config;
        
        // Afficher les informations
        modalBody.innerHTML = `
            <div class="config-group">
                <h3>📋 Instance</h3>
                <div class="config-item">
                    <span class="config-label">Domaine:</span>
                    <span class="config-value">${config.domain}</span>
                </div>
                <div class="config-item">
                    <span class="config-label">URL:</span>
                    <span class="config-value"><a href="${config.url}" target="_blank">${config.url}</a></span>
                </div>
                <div class="config-item">
                    <span class="config-label">Créée le:</span>
                    <span class="config-value">${new Date(config.created_at).toLocaleString('fr-FR')}</span>
                </div>
            </div>
            
            <div class="config-group">
                <h3>🗄️ Base de données</h3>
                <div class="config-item">
                    <span class="config-label">Host:</span>
                    <span class="config-value">${config.database.host}</span>
                </div>
                <div class="config-item">
                    <span class="config-label">Database:</span>
                    <span class="config-value">${config.database.name}</span>
                </div>
                <div class="config-item">
                    <span class="config-label">User:</span>
                    <span class="config-value">${config.database.user}</span>
                </div>
                <div class="config-item">
                    <span class="config-label">Password:</span>
                    <span class="config-value">${config.database.password}</span>
                </div>
            </div>
            
            <div class="config-group">
                <h3>📁 Chemins</h3>
                <div class="config-item">
                    <span class="config-label">Root:</span>
                    <span class="config-value">${config.paths.root}</span>
                </div>
                <div class="config-item">
                    <span class="config-label">Htdocs:</span>
                    <span class="config-value">${config.paths.htdocs}</span>
                </div>
                <div class="config-item">
                    <span class="config-label">Documents:</span>
                    <span class="config-value">${config.paths.documents}</span>
                </div>
            </div>
        `;
        
    } catch (error) {
        modalBody.innerHTML = `<div class="error">❌ Erreur: ${error.message}</div>`;
    }
};

/**
 * Ferme la modale et nettoie les données
 */
function closeConfigModal() {
    const modal = document.getElementById('configModal');
    const modalBody = document.getElementById('configModalBody');
    
    // Cacher la modale
    modal.classList.add('hidden');
    
    // Nettoyer le contenu (sécurité anti-XSS)
    modalBody.innerHTML = '';
}

// Gestionnaire de fermeture de la modale
document.getElementById('closeModal').addEventListener('click', closeConfigModal);

// Fermer la modale en cliquant sur le fond
document.getElementById('configModal').addEventListener('click', (e) => {
    if (e.target.id === 'configModal') {
        closeConfigModal();
    }
});

/**
 * Lance un backup d'une instance
 */
async function startBackup(domain) {
    const modal = document.getElementById('backupModal');
    const modalBody = document.getElementById('backupModalBody');
    const logsDiv = document.getElementById('backupProgressLogs');
    
    // Afficher la modal
    modal.classList.remove('hidden');
    modalBody.innerHTML = '<p>Créer un backup complet de <strong>' + domain + '</strong> ?</p><button class="btn btn-primary" id="confirmBackup">Créer le backup</button><div id="backupProgressContainer" class="hidden"><div class="progress-container"><div class="progress-bar animated" id="backupProgressBar"><span class="spinner"></span><span class="progress-bar-text" id="backupProgressText">Initialisation...</span></div></div></div><div class="progress-logs" id="backupProgressLogs"></div>';
    
    document.getElementById('confirmBackup').addEventListener('click', async () => {
        document.getElementById('confirmBackup').disabled = true;
        document.getElementById('confirmBackup').style.display = 'none';
        document.getElementById('backupProgressContainer').classList.remove('hidden');
        logsDiv.innerHTML = '<div class="log-step">📦 Démarrage du backup...</div>';
        
        try {
            // Lancer le backup
            const response = await fetch(`${API_BASE}/api/instances/${domain}/backup`, {
                method: 'POST'
            });
            const data = await response.json();
            
            if (data.success) {
                // Écouter les événements SSE
                const eventSource = new EventSource(`${API_BASE}/api/events/${data.jobId}`);
                
                const progressBar = document.getElementById('backupProgressBar');
                const progressText = document.getElementById('backupProgressText');
                let progress = 0;
                
                eventSource.onmessage = (event) => {
                    const eventData = JSON.parse(event.data);
                    
                    switch (eventData.type) {
                        case 'connected':
                            logsDiv.innerHTML += '<div class="log-step">🔌 Connecté au serveur</div>';
                            progress = 10;
                            progressBar.style.width = progress + '%';
                            progressText.textContent = 'Connexion établie...';
                            break;
                        case 'step':
                            logsDiv.innerHTML += `<div class="log-step">${eventData.message}</div>`;
                            if (eventData.step === 'prepare') {
                                progress = 20;
                                progressText.textContent = 'Préparation...';
                            } else if (eventData.step === 'backup') {
                                progress = 40;
                                progressText.textContent = 'Création du backup...';
                            }
                            progressBar.style.width = progress + '%';
                            break;
                        case 'log':
                            logsDiv.innerHTML += `<div class="log-line">${eventData.message}</div>`;
                            if (progress < 90) {
                                progress += 10;
                                progressBar.style.width = progress + '%';
                            }
                            break;
                        case 'complete':
                            logsDiv.innerHTML += `<div class="log-complete">${eventData.message}</div>`;
                            progress = 100;
                            progressBar.style.width = '100%';
                            progressText.textContent = '✅ Terminé !';
                            progressBar.classList.remove('animated');
                            eventSource.close();
                            setTimeout(() => {
                                closeBackupModal();
                                // Ne recharger que cette instance
                                refreshInstance(domain);
                            }, 2000);
                            break;
                        case 'error':
                            logsDiv.innerHTML += `<div class="log-error">❌ Erreur: ${eventData.error}</div>`;
                            progressBar.style.width = '100%';
                            progressBar.style.background = '#e74c3c';
                            progressText.textContent = '❌ Erreur';
                            progressBar.classList.remove('animated');
                            eventSource.close();
                            break;
                    }
                    
                    // Auto-scroll
                    logsDiv.scrollTop = logsDiv.scrollHeight;
                };
                
                eventSource.onerror = () => {
                    logsDiv.innerHTML += '<div class="log-error">❌ Connexion perdue</div>';
                    eventSource.close();
                };
            } else {
                logsDiv.innerHTML = `<div class="log-error">❌ ${data.error}</div>`;
            }
        } catch (error) {
            logsDiv.innerHTML = `<div class="log-error">❌ Erreur: ${error.message}</div>`;
        }
    });
}

/**
 * Affiche la modal de restore avec liste des backups
 */
async function showRestore(domain) {
    const modal = document.getElementById('restoreModal');
    const modalBody = document.getElementById('restoreModalBody');
    
    // Afficher la modal
    modal.classList.remove('hidden');
    modalBody.innerHTML = '<div class="loading">Chargement des backups...</div>';
    
    try {
        const response = await fetch(`${API_BASE}/api/instances/${domain}/backups`);
        const data = await response.json();
        
        if (data.success && data.backups.length > 0) {
            modalBody.innerHTML = `
                <p>Sélectionnez un backup à restaurer pour <strong>${domain}</strong> :</p>
                <div class="backups-list">
                    ${data.backups.map(backup => {
                        // Lire la version depuis le manifest
                        const version = backup.manifest && backup.manifest.dolibarrVersion ? backup.manifest.dolibarrVersion : null;
                        
                        return `
                        <div class="backup-item" onclick="selectBackup('${domain}', '${backup.timestamp}', this)">
                            <div class="backup-date">📅 ${new Date(backup.created).toLocaleString('fr-FR')}</div>
                            ${version ? `<div class="backup-version">📦 Version ${version}</div>` : ''}
                            <div class="backup-size">💾 ${backup.sizeMB} MB</div>
                        </div>
                    `}).join('')}
                </div>
                <div class="restore-options">
                    <label>
                        <input type="checkbox" id="restoreServerConfigCheck">
                        Restaurer aussi la configuration serveur (Apache/Nginx/SSL)
                    </label>
                </div>
                <button class="btn btn-primary" id="confirmRestore" disabled>Restaurer</button>
                <div class="progress-logs" id="restoreProgressLogs"></div>
            `;
        } else if (data.success && data.backups.length === 0) {
            modalBody.innerHTML = `<div class="error">Aucun backup disponible pour ${domain}</div>`;
        } else {
            modalBody.innerHTML = `<div class="error">❌ ${data.error}</div>`;
        }
    } catch (error) {
        modalBody.innerHTML = `<div class="error">❌ Erreur: ${error.message}</div>`;
    }
}

/**
 * Sélectionne un backup dans la liste
 */
let selectedBackup = null;
window.selectBackup = function(domain, timestamp, element) {
    // Désélectionner tous
    document.querySelectorAll('.backup-item').forEach(item => item.classList.remove('selected'));
    
    // Sélectionner celui-ci
    element.classList.add('selected');
    selectedBackup = { domain, timestamp };
    
    // Activer le bouton restore
    const confirmBtn = document.getElementById('confirmRestore');
    if (confirmBtn) {
        confirmBtn.disabled = false;
        confirmBtn.onclick = () => executeRestore(domain, timestamp);
    }
};

/**
 * Execute la restauration
 */
async function executeRestore(domain, timestamp) {
    const logsDiv = document.getElementById('restoreProgressLogs');
    const restoreServerConfig = document.getElementById('restoreServerConfigCheck').checked;
    
    document.getElementById('confirmRestore').disabled = true;
    document.getElementById('confirmRestore').style.display = 'none';
    
    // Ajouter la barre de progression
    logsDiv.innerHTML = '<div class="progress-container"><div class="progress-bar animated" id="restoreProgressBar"><span class="spinner"></span><span class="progress-bar-text" id="restoreProgressText">Initialisation...</span></div></div><div id="restoreLogs"></div>';
    
    const restoreLogs = document.getElementById('restoreLogs');
    restoreLogs.innerHTML = '<div class="log-step">♻️ Démarrage de la restauration...</div>';
    
    try {
        const response = await fetch(`${API_BASE}/api/instances/${domain}/restore`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ timestamp, restoreServerConfig })
        });
        const data = await response.json();
        
        if (data.success) {
            // Écouter les événements SSE
            const eventSource = new EventSource(`${API_BASE}/api/events/${data.jobId}`);
            const progressBar = document.getElementById('restoreProgressBar');
            const progressText = document.getElementById('restoreProgressText');
            let progress = 0;
            
            eventSource.onmessage = (event) => {
                const eventData = JSON.parse(event.data);
                
                switch (eventData.type) {
                    case 'connected':
                        restoreLogs.innerHTML += '<div class="log-step">🔌 Connecté au serveur</div>';
                        progress = 10;
                        progressBar.style.width = progress + '%';
                        progressText.textContent = 'Connexion établie...';
                        break;
                    case 'step':
                        restoreLogs.innerHTML += `<div class="log-step">${eventData.message}</div>`;
                        if (eventData.step === 'prepare') {
                            progress = 20;
                            progressText.textContent = 'Préparation...';
                        } else if (eventData.step === 'restore') {
                            progress = 50;
                            progressText.textContent = 'Restauration en cours...';
                        }
                        progressBar.style.width = progress + '%';
                        break;
                    case 'log':
                        restoreLogs.innerHTML += `<div class="log-line">${eventData.message}</div>`;
                        if (progress < 90) {
                            progress += 8;
                            progressBar.style.width = progress + '%';
                        }
                        break;
                    case 'complete':
                        restoreLogs.innerHTML += `<div class="log-complete">${eventData.message}</div>`;
                        progress = 100;
                        progressBar.style.width = '100%';
                        progressText.textContent = '✅ Terminé !';
                        progressBar.classList.remove('animated');
                        eventSource.close();
                        setTimeout(() => {
                            closeRestoreModal();
                            // Ne rafraîchir que l'instance restaurée
                            refreshInstance(domain);
                        }, 2000);
                        break;
                    case 'error':
                        restoreLogs.innerHTML += `<div class="log-error">❌ Erreur: ${eventData.error}</div>`;
                        progressBar.style.width = '100%';
                        progressBar.style.background = '#e74c3c';
                        progressText.textContent = '❌ Erreur';
                        progressBar.classList.remove('animated');
                        eventSource.close();
                        break;
                }
                
                // Auto-scroll
                logsDiv.scrollTop = logsDiv.scrollHeight;
            };
            
            eventSource.onerror = () => {
                logsDiv.innerHTML += '<div class="log-error">❌ Connexion perdue</div>';
                eventSource.close();
            };
        } else {
            logsDiv.innerHTML = `<div class="log-error">❌ ${data.error}</div>`;
        }
    } catch (error) {
        logsDiv.innerHTML = `<div class="log-error">❌ Erreur: ${error.message}</div>`;
    }
}

/**
 * Ferme la modal de backup
 */
function closeBackupModal() {
    const modal = document.getElementById('backupModal');
    const modalBody = document.getElementById('backupModalBody');
    modal.classList.add('hidden');
    modalBody.innerHTML = '';
}

/**
 * Ferme la modal de restore
 */
function closeRestoreModal() {
    const modal = document.getElementById('restoreModal');
    const modalBody = document.getElementById('restoreModalBody');
    modal.classList.add('hidden');
    modalBody.innerHTML = '';
    selectedBackup = null;
}

// Gestionnaires de fermeture des modals
document.getElementById('closeBackupModal').addEventListener('click', closeBackupModal);
document.getElementById('closeRestoreModal').addEventListener('click', closeRestoreModal);

// Fermer en cliquant sur le fond
document.getElementById('backupModal').addEventListener('click', (e) => {
    if (e.target.id === 'backupModal') closeBackupModal();
});
document.getElementById('restoreModal').addEventListener('click', (e) => {
    if (e.target.id === 'restoreModal') closeRestoreModal();
});

// Exposer les fonctions globalement pour les onclick inline
window.startBackup = startBackup;
window.showRestore = showRestore;
window.showConfig = showConfig;

/**
 * Chargement initial
 */
(async () => {
    const authenticated = await checkAuth();
    if (authenticated) {
        loadDomains();
        loadInstances();
    }
})();

/**
 * Charge les informations de version pour toutes les instances
 */
// Removed: loadVersions - now using loadInstanceDetails for progressive loading

/**
 * Démarre l'upgrade d'une instance
 */
window.startUpgrade = async function(domain, currentVersion, targetVersion) {
    if (!confirm(`Mettre à jour ${domain} de ${currentVersion} vers ${targetVersion} ?\n\nUn backup automatique sera effectué avant la mise à jour.`)) {
        return;
    }
    
    // Nettoyer et afficher la section logs de l'instance
    const logsContent = document.getElementById(`logs-content-${domain}`);
    if (logsContent) {
        logsContent.innerHTML = '';
    }
    
    addInstanceLog(domain, `🔄 Démarrage de la mise à jour de ${domain}...`, 'step');
    
    try {
        const response = await fetch(`${API_BASE}/api/instances/${domain}/upgrade`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ targetVersion })
        });
        
        const data = await response.json();
        
        if (!data.success) {
            throw new Error(data.error);
        }
        
        const jobId = data.jobId;
        addInstanceLog(domain, `Job ID: ${jobId}`, 'info');
        
        // Suivre la progression via SSE
        const eventSource = new EventSource(`${API_BASE}/api/events/${jobId}`);
        currentEventSource = eventSource;
        
        eventSource.onmessage = (event) => {
            const data = JSON.parse(event.data);
            
            if (data.type === 'step') {
                addInstanceLog(domain, `${data.message}`, 'step');
            } else if (data.type === 'log') {
                addInstanceLog(domain, data.message, 'log');
            } else if (data.type === 'complete') {
                addInstanceLog(domain, `✅ ${data.message}`, 'success');
                eventSource.close();
                
                // Afficher le bouton [x]
                const closeBtn = document.getElementById(`close-logs-${domain}`);
                if (closeBtn) {
                    closeBtn.style.display = 'inline-block';
                }
                
                // Afficher le modal avec les instructions
                showUpgradeInstructions(domain, data.upgradeInfo);
            } else if (data.type === 'error') {
                addInstanceLog(domain, `❌ Erreur: ${data.error}`, 'error');
                eventSource.close();
                
                // Afficher le bouton [x] aussi en cas d'erreur
                const closeBtn = document.getElementById(`close-logs-${domain}`);
                if (closeBtn) {
                    closeBtn.style.display = 'inline-block';
                }
            }
        };
        
        eventSource.onerror = () => {
            addInstanceLog(domain, '❌ Connexion au serveur perdue', 'error');
            eventSource.close();
        };
        
    } catch (error) {
        addInstanceLog(domain, `❌ Erreur: ${error.message}`, 'error');
    }
};

/**
 * Affiche le modal avec les instructions post-upgrade
 */
function showUpgradeInstructions(domain, upgradeInfo) {
    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.id = 'upgradeModal';
    modal.innerHTML = `
        <div class="modal-content">
            <h2>🎉 Mise à jour préparée</h2>
            <p>Les fichiers ont été mis à jour. Suivez maintenant ces étapes :</p>
            
            <div class="upgrade-steps">
                <div class="upgrade-step">
                    <div class="step-number">1</div>
                    <div class="step-content">
                        <strong>Visitez l'URL suivante :</strong>
                        <a href="${upgradeInfo.nextSteps.url}" target="_blank" class="upgrade-url">
                            ${upgradeInfo.nextSteps.url}
                        </a>
                    </div>
                </div>
                
                <div class="upgrade-step">
                    <div class="step-number">2</div>
                    <div class="step-content">
                        <strong>Cliquez sur "Update"</strong> dans le menu proposé
                    </div>
                </div>
                
                <div class="upgrade-step">
                    <div class="step-number">3</div>
                    <div class="step-content">
                        <strong>Suivez les étapes</strong> de mise à jour jusqu'à la fin
                        <small>Le processus peut boucler si plusieurs versions de retard</small>
                    </div>
                </div>
                
                <div class="upgrade-step">
                    <div class="step-number">4</div>
                    <div class="step-content">
                        <strong>Une fois terminé</strong>, verrouillez l'instance en cliquant sur le cadenas 🔒
                    </div>
                </div>
            </div>
            
            <div class="backup-info">
                ℹ️ Un backup a été créé : <code>${upgradeInfo.backup ? upgradeInfo.backup.file : 'N/A'}</code> (${upgradeInfo.backup ? upgradeInfo.backup.size : '?'} MB)
            </div>
            
            <div class="modal-actions">
                <button class="btn btn-primary" onclick="closeUpgradeModal('${domain}')">✅ Fermer</button>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    
    // Fermer en cliquant en dehors
    modal.addEventListener('click', (e) => {
        if (e.target.id === 'upgradeModal') closeUpgradeModal(domain);
    });
}

/**
 * Ferme le modal upgrade et rafraîchit l'instance
 */
window.closeUpgradeModal = async function(domain) {
    const modal = document.getElementById('upgradeModal');
    if (modal) modal.remove();
    
    // Rafraîchir uniquement cette instance
    if (domain) {
        await refreshInstance(domain);
    }
};

/**
 * Complete l'upgrade en recréant install.lock
 */
// Removed: loadLockStatus - now using loadInstanceDetails for progressive loading

/**
 * Toggle le verrou d'une instance
 */
window.toggleLock = async function(domain, currentLockState) {
    const newLockState = !currentLockState;
    
    try {
        const response = await fetch(`${API_BASE}/api/instances/${domain}/lock`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ locked: newLockState })
        });
        
        const data = await response.json();
        
        if (data.success) {
            // Recharger uniquement cette instance
            await refreshInstance(domain);
        } else {
            alert(`Erreur: ${data.error}`);
        }
    } catch (error) {
        alert(`Erreur: ${error.message}`);
    }
};
